// Iter-194u — CEO Settings · Invoice Payment Reminders
//
// Exposes all CRON parameters previously buried in MongoDB:
//   • Toggle enabled / overdue→CEO email
//   • Milestones for 30/60/90-day delays (3 ints ascending each)
//   • Custom delay buckets (e.g. 45 days → [25, 35, 42])
//   • Subject + body templates for both milestone reminders and the terminal
//     overdue escalation, with a mustache-variables cheat-sheet.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, AlertCircle, Plus, X, Loader2, Mail, Pause, Search, Building2, Briefcase, Eye, ExternalLink } from "lucide-react";
import CTAInsertionHint from "@/components/CTAInsertionHint";
import { SettingsHeader } from "./_shared";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SCOPE_LABEL = { ALL: "All", MILESTONE: "M", OVERDUE: "OD" };
const SCOPE_FULL = {
  ALL: "Milestones + Dépassement",
  MILESTONE: "Milestones uniquement",
  OVERDUE: "Dépassement uniquement",
};

const VARS_HINT = `Variables disponibles : {{client_name}}, {{contact_gender}}, {{contact_name}}, {{invoice_number}}, {{due_date}}, {{amount_ttc}}, {{currency}}, {{days_since_sent}}, {{days_overdue}}, {{milestone}}, {{tier}}, {{deadline_label}}, {{sent_at_short}}, {{payment_declaration_url}}`;

export default function InvoiceRemindersSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/invoice-payment-reminders/settings");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec chargement");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!data.enabled,
        overdue_email_to_ceo_enabled: !!data.overdue_email_to_ceo_enabled,
        milestones_30d: data.milestones_30d.map(Number),
        milestones_60d: data.milestones_60d.map(Number),
        milestones_90d: data.milestones_90d.map(Number),
        custom_milestones: data.custom_milestones || {},
        subject_template_milestone: data.subject_template_milestone || "",
        body_template_milestone: data.body_template_milestone || "",
        subject_template_overdue: data.subject_template_overdue || "",
        body_template_overdue: data.body_template_overdue || "",
        from_email_milestone: data.from_email_milestone || "",
        from_email_overdue: data.from_email_overdue || "",
        // Iter-200d — declarant confirmation email + Cc.
        payment_declaration_confirmation_enabled: !!data.payment_declaration_confirmation_enabled,
        payment_declaration_confirmation_cc_email: data.payment_declaration_confirmation_cc_email || "",
        // Iter-201 — recurring overdue resend cadence (0 = no repeat).
        overdue_resend_days: Number.isFinite(parseInt(data.overdue_resend_days, 10))
          ? parseInt(data.overdue_resend_days, 10) : 0,
        // Iter-206h — CEO-configurable UTC hour for the daily cron.
        reminder_cron_hour_utc: Number.isFinite(parseInt(data.reminder_cron_hour_utc, 10)) ? parseInt(data.reminder_cron_hour_utc, 10) : 7,
        reminder_cron_minute_utc: Number.isFinite(parseInt(data.reminder_cron_minute_utc, 10)) ? parseInt(data.reminder_cron_minute_utc, 10) : 15,
        overdue_cron_hour_utc: Number.isFinite(parseInt(data.overdue_cron_hour_utc, 10)) ? parseInt(data.overdue_cron_hour_utc, 10) : 7,
        overdue_cron_minute_utc: Number.isFinite(parseInt(data.overdue_cron_minute_utc, 10)) ? parseInt(data.overdue_cron_minute_utc, 10) : 15,
        // Iter-228x — Invoice paid / thank-you template (NEW section).
        paid_thankyou_enabled: !!data.paid_thankyou_enabled,
        paid_thankyou_subject_template: data.paid_thankyou_subject_template || "",
        paid_thankyou_body_template: data.paid_thankyou_body_template || "",
        paid_thankyou_extra_cc: data.paid_thankyou_extra_cc || "",
      };
      const r = await api.put("/admin/invoice-payment-reminders/settings", payload);
      setData(r.data);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec sauvegarde");
    } finally { setSaving(false); }
  };

  if (!data) {
    return (
      <div className="p-8 text-center text-sm text-zinc-500">
        {loading ? <><Loader2 size={16} className="animate-spin inline mr-2" />Chargement…</> : "Aucune donnée."}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl" data-testid="invoice-reminders-settings">
      <SettingsHeader
        kicker="Billing"
        title="Relances factures clients"
        description="Configurez les délais de relance, les milestones et les templates d'email envoyés automatiquement avant et après l'échéance d'une facture client."
      />

      {/* Toggles */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-zinc-900">Cron actif</div>
            <div className="text-xs text-zinc-500">Désactivé : les emails de relance ne sont jamais envoyés (les badges UI restent).</div>
          </div>
          <Switch
            checked={!!data.enabled}
            onCheckedChange={(v) => patch("enabled", v)}
            data-testid="reminder-enabled"
          />
        </div>
        <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <div>
            <div className="text-sm font-semibold text-zinc-900">Email CEO sur dépassement</div>
            <div className="text-xs text-zinc-500">Envoie un email d'escalade au CEO quand une facture passe en retard.</div>
          </div>
          <Switch
            checked={!!data.overdue_email_to_ceo_enabled}
            onCheckedChange={(v) => patch("overdue_email_to_ceo_enabled", v)}
            data-testid="reminder-overdue-ceo"
          />
        </div>
        {/* Iter-200d — auto-confirmation email to the declarant after each client-initiated declaration. */}
        <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <div className="flex-1 pr-4">
            <div className="text-sm font-semibold text-zinc-900">Accusé de réception déclarant (avec PDF)</div>
            <div className="text-xs text-zinc-500">Envoie un email + récap PDF au déclarant à chaque déclaration de paiement client. Cc paramétrable ci-dessous.</div>
          </div>
          <Switch
            checked={!!data.payment_declaration_confirmation_enabled}
            onCheckedChange={(v) => patch("payment_declaration_confirmation_enabled", v)}
            data-testid="payment-decl-confirmation-enabled"
          />
        </div>
        {data.payment_declaration_confirmation_enabled && (
          <div className="border-t border-zinc-100 pt-4">
            <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">
              Cc accusé de réception (interne BAIM)
            </Label>
            <Input
              value={data.payment_declaration_confirmation_cc_email || ""}
              onChange={(e) => patch("payment_declaration_confirmation_cc_email", e.target.value)}
              placeholder="facturation@ba-inmotion.com"
              type="email"
              data-testid="payment-decl-confirmation-cc"
            />
            <div className="text-[10px] text-zinc-500 font-mono mt-1">
              Cette adresse est ajoutée en Cc de chaque accusé de réception envoyé au déclarant client. Laisser vide pour n'envoyer qu'au déclarant.
            </div>
          </div>
        )}
      </div>

      {/* Iter-195o — Exclusions panel: pause the CRON for specific clients
          or missions while keeping the global toggle ON. Mirrors the
          End-of-month auto-invoice "exceptions" pattern. */}
      <ExclusionsPanel />

      {/* Iter-228x — Email de remerciement automatique après règlement.
          Envoyé une seule fois par facture (idempotent), avec gating sur
          `paid_at`. Le template est éditable comme les autres relances. */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-3" data-testid="paid-thankyou-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-zinc-900">Email automatique après règlement</div>
            <div className="text-xs text-zinc-500">
              Envoyé au contact client lorsque la facture passe en payée (par BAIM ou via le formulaire de déclaration).
              CC : CEO + Gestionnaires + Extra CC ci-dessous.
            </div>
          </div>
          <Switch
            checked={!!data.paid_thankyou_enabled}
            onCheckedChange={(v) => patch("paid_thankyou_enabled", v)}
            data-testid="paid-thankyou-enabled"
          />
        </div>
        {data.paid_thankyou_enabled && (
          <>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Sujet</Label>
              <Input
                value={data.paid_thankyou_subject_template || ""}
                onChange={(e) => patch("paid_thankyou_subject_template", e.target.value)}
                placeholder="[BAIM] Facture {{invoice_number}} réglée — Merci !"
                data-testid="paid-thankyou-subject"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Corps</Label>
              <textarea
                value={data.paid_thankyou_body_template || ""}
                onChange={(e) => patch("paid_thankyou_body_template", e.target.value)}
                rows={8}
                placeholder={"Bonjour {{contact_gender}} {{contact_name}},\n\nNous accusons bonne réception de votre règlement…"}
                data-testid="paid-thankyou-body"
                className="w-full font-mono text-xs border border-zinc-200 rounded-md p-2"
              />
              <div className="text-[10px] text-zinc-500 font-mono mt-1">
                Variables : <code>{"{{invoice_number}}"}</code> · <code>{"{{client_name}}"}</code> · <code>{"{{contact_name}}"}</code> · <code>{"{{contact_gender}}"}</code> · <code>{"{{mission_title}}"}</code> · <code>{"{{amount_ttc}}"}</code> · <code>{"{{paid_amount}}"}</code> · <code>{"{{currency}}"}</code> · <code>{"{{paid_at}}"}</code>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                Laisser vide pour utiliser le template par défaut (français, courtois, avec remerciements).
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Extra CC (séparés par virgule ou point-virgule)</Label>
              <Input
                value={data.paid_thankyou_extra_cc || ""}
                onChange={(e) => patch("paid_thankyou_extra_cc", e.target.value)}
                placeholder="compta@ba-inmotion.com"
                data-testid="paid-thankyou-extra-cc"
              />
            </div>
          </>
        )}
      </div>

      {/* Canonical milestones */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-5">
        <div>
          <div className="text-sm font-semibold text-zinc-900">Milestones par délai (en jours après envoi)</div>
          <div className="text-xs text-zinc-500">3 valeurs strictement croissantes, entre 1 et 365.</div>
        </div>
        <MilestonesRow
          label="Délai 30 jours"
          value={data.milestones_30d}
          onChange={(v) => patch("milestones_30d", v)}
          testid="ms-30d"
        />
        <MilestonesRow
          label="Délai 60 jours"
          value={data.milestones_60d}
          onChange={(v) => patch("milestones_60d", v)}
          testid="ms-60d"
        />
        <MilestonesRow
          label="Délai 90 jours"
          value={data.milestones_90d}
          onChange={(v) => patch("milestones_90d", v)}
          testid="ms-90d"
        />
      </div>

      {/* Custom buckets */}
      <CustomMilestones
        custom={data.custom_milestones || {}}
        onChange={(v) => patch("custom_milestones", v)}
      />

      {/* Templates */}
      <TemplateBlock
        title="Email de relance (milestones)"
        kind="milestone"
        subject={data.subject_template_milestone}
        body={data.body_template_milestone}
        fromEmail={data.from_email_milestone}
        onSubject={(v) => patch("subject_template_milestone", v)}
        onBody={(v) => patch("body_template_milestone", v)}
        onFromEmail={(v) => patch("from_email_milestone", v)}
        testid="tpl-milestone"
      />
      <TemplateBlock
        title="Email de dépassement (overdue)"
        kind="overdue"
        subject={data.subject_template_overdue}
        body={data.body_template_overdue}
        fromEmail={data.from_email_overdue}
        onSubject={(v) => patch("subject_template_overdue", v)}
        onBody={(v) => patch("body_template_overdue", v)}
        onFromEmail={(v) => patch("from_email_overdue", v)}
        testid="tpl-overdue"
      />

      {/* Iter-201 — Recurring overdue resend cadence */}
      <OverdueRecurringBlock
        days={data.overdue_resend_days || 0}
        onChange={(v) => patch("overdue_resend_days", v)}
      />

      {/* Iter-206h — CEO-configurable UTC hour for the daily reminder cron. */}
      <CronScheduleBlock
        reminderH={data.reminder_cron_hour_utc ?? 7}
        reminderM={data.reminder_cron_minute_utc ?? 15}
        overdueH={data.overdue_cron_hour_utc ?? 7}
        overdueM={data.overdue_cron_minute_utc ?? 15}
        onChange={(k, v) => patch(k, parseInt(v, 10))}
      />

      <div className="flex justify-end sticky bottom-2">
        <button
          onClick={save}
          disabled={saving}
          data-testid="reminder-save-btn"
          className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50 shadow-lg"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

function OverdueRecurringBlock({ days, onChange }) {
  // Iter-201 — recurring overdue cadence UI. 0 disables repetition.
  const [val, setVal] = useState(days);
  useEffect(() => { setVal(days); }, [days]);
  const update = (raw) => {
    const n = raw === "" ? 0 : Math.max(0, Math.min(365, parseInt(raw, 10) || 0));
    setVal(n);
    onChange(n);
  };
  const enabled = val > 0;
  return (
    <div
      className="bg-white border border-zinc-200 rounded-lg p-5 space-y-3"
      data-testid="overdue-recurring-block"
    >
      <div className="flex items-start gap-2">
        <Mail size={14} className="text-zinc-500 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-900">Cadence des relances de dépassement</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Après l'email de dépassement initial, renvoyer un rappel automatique tous les <strong>X jours ouvrés</strong> (week-ends et jours fériés exclus) tant que la facture n'est pas marquée comme payée.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
        <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 shrink-0">Tous les</Label>
        <Input
          type="number"
          min={0}
          max={365}
          value={val ?? 0}
          onChange={(e) => update(e.target.value)}
          data-testid="overdue-resend-days-input"
          className="w-24 text-center"
        />
        <span className="text-xs text-zinc-700 font-mono">jours ouvrés</span>
        <div className={`ml-auto text-[11px] font-semibold rounded-full px-2.5 py-1 ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
          {enabled ? `Actif · répétition tous les ${val} j ouvrés` : "Désactivé (envoi unique)"}
        </div>
      </div>
      <div className="text-[10px] text-zinc-400 font-mono flex items-start gap-1.5">
        <AlertCircle size={11} className="shrink-0 mt-0.5" />
        <span>0 = pas de répétition (comportement historique : un seul email à l'échéance dépassée). Le compteur n'avance que sur les jours ouvrés (Lun-Ven hors jours fériés Maroc). Recommandation : 5 à 10 jours ouvrés.</span>
      </div>
    </div>
  );
}


// Iter-206h — CEO-configurable UTC hour for the daily reminder cron and the
// overdue cron. Stored in app_settings as `reminder_cron_hour_utc`,
// `reminder_cron_minute_utc`, `overdue_cron_hour_utc`, `overdue_cron_minute_utc`.
function CronScheduleBlock({ reminderH, reminderM, overdueH, overdueM, onChange }) {
  const cur = new Date();
  const utcNow = `${String(cur.getUTCHours()).padStart(2, "0")}:${String(cur.getUTCMinutes()).padStart(2, "0")}`;
  const TimeInput = ({ h, m, onH, onM, testidPrefix }) => (
    <div className="flex items-center gap-1.5">
      <input
        type="number" min={0} max={23}
        value={h} onChange={(e) => onH(e.target.value)}
        className="w-14 px-2 py-1.5 border border-zinc-300 rounded text-sm font-mono text-right"
        data-testid={`${testidPrefix}-h`}
      />
      <span className="text-zinc-400 font-mono">:</span>
      <input
        type="number" min={0} max={59}
        value={m} onChange={(e) => onM(e.target.value)}
        className="w-14 px-2 py-1.5 border border-zinc-300 rounded text-sm font-mono text-right"
        data-testid={`${testidPrefix}-m`}
      />
      <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">UTC</span>
    </div>
  );
  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-white" data-testid="cron-schedule-block">
      <h3 className="font-heading font-bold text-zinc-900 flex items-center gap-2">Planification UTC du cron</h3>
      <p className="text-xs text-zinc-500 mt-1">
        Heure d'exécution quotidienne du job de relances. Tous les fuseaux internes sont en UTC.
        Heure actuelle UTC&nbsp;: <strong className="font-mono">{utcNow}</strong>.
        Modification appliquée immédiatement sans redémarrage.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1.5">Relances par paliers (J+20, J+25, etc.)</label>
          <TimeInput
            h={reminderH} m={reminderM}
            onH={(v) => onChange("reminder_cron_hour_utc", v)}
            onM={(v) => onChange("reminder_cron_minute_utc", v)}
            testidPrefix="reminder-cron"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1.5">Dépassement (overdue + résends)</label>
          <TimeInput
            h={overdueH} m={overdueM}
            onH={(v) => onChange("overdue_cron_hour_utc", v)}
            onM={(v) => onChange("overdue_cron_minute_utc", v)}
            testidPrefix="overdue-cron"
          />
          <p className="text-[10px] text-zinc-400 mt-1 italic">
            Astuce : utilisez 09h00 UTC si vos rapprochements bancaires arrivent en début de matinée européenne.
          </p>
        </div>
      </div>
    </div>
  );
}



function MilestonesRow({ label, value, onChange, testid }) {
  const [arr, setArr] = useState(value || [0, 0, 0]);
  useEffect(() => { setArr(value || [0, 0, 0]); }, [value]);
  const update = (i, v) => {
    const next = [...arr];
    next[i] = v === "" ? "" : Math.max(1, Math.min(365, parseInt(v, 10) || 0));
    setArr(next);
    onChange(next);
  };
  return (
    <div className="flex items-center justify-between gap-3" data-testid={`milestones-row-${testid}`}>
      <Label className="text-xs font-mono text-zinc-700 w-28 shrink-0">{label}</Label>
      <div className="flex gap-2 items-center">
        {[0, 1, 2].map((i) => (
          <Input
            key={i}
            type="number"
            min={1}
            max={365}
            value={arr[i] ?? ""}
            onChange={(e) => update(i, e.target.value)}
            data-testid={`${testid}-${i}`}
            className="w-20 text-center"
          />
        ))}
        <span className="text-[11px] text-zinc-400 font-mono">jours</span>
      </div>
    </div>
  );
}

function CustomMilestones({ custom, onChange }) {
  const entries = Object.entries(custom || {});
  const [newDays, setNewDays] = useState("");
  const addEntry = () => {
    const d = parseInt(newDays, 10);
    if (!d || d < 1 || d > 365) {
      toast.error("Délai invalide (1-365)");
      return;
    }
    if (custom[String(d)]) {
      toast.error("Délai déjà défini");
      return;
    }
    const guess = [
      Math.max(1, Math.round(d * 0.6)),
      Math.max(2, Math.round(d * 0.8)),
      Math.max(3, Math.round(d * 0.95)),
    ];
    onChange({ ...custom, [String(d)]: guess });
    setNewDays("");
  };
  const removeEntry = (k) => {
    const next = { ...custom };
    delete next[k];
    onChange(next);
  };
  const updateEntry = (k, idx, v) => {
    const next = { ...custom };
    const row = [...(next[k] || [0, 0, 0])];
    row[idx] = v === "" ? "" : Math.max(1, Math.min(365, parseInt(v, 10) || 0));
    next[k] = row;
    onChange(next);
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-3" data-testid="custom-milestones-block">
      <div>
        <div className="text-sm font-semibold text-zinc-900">Délais custom (en jours)</div>
        <div className="text-xs text-zinc-500">Pour les délais qui ne sont ni 30, ni 60, ni 90 jours. Si non défini → fallback à 60% / 80% / 95% du délai.</div>
      </div>
      {entries.length === 0 && <div className="text-xs italic text-zinc-400">Aucun délai custom configuré.</div>}
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-3" data-testid={`custom-row-${k}`}>
          <div className="text-xs font-mono text-zinc-700 w-20 shrink-0">{k} j</div>
          <div className="flex gap-2 items-center flex-1">
            {[0, 1, 2].map((i) => (
              <Input
                key={i}
                type="number"
                min={1}
                max={365}
                value={(v || [])[i] ?? ""}
                onChange={(e) => updateEntry(k, i, e.target.value)}
                data-testid={`custom-${k}-${i}`}
                className="w-20 text-center"
              />
            ))}
            <span className="text-[11px] text-zinc-400 font-mono">jours</span>
          </div>
          <button
            onClick={() => removeEntry(k)}
            className="text-zinc-400 hover:text-red-600 p-1"
            data-testid={`custom-remove-${k}`}
          ><X size={14} /></button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
        <Input
          type="number"
          min={1}
          max={365}
          placeholder="Nouveau délai (ex. 45)"
          value={newDays}
          onChange={(e) => setNewDays(e.target.value)}
          data-testid="custom-new-days"
          className="w-44"
        />
        <button
          onClick={addEntry}
          data-testid="custom-add-btn"
          className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50"
        >
          <Plus size={12} /> Ajouter
        </button>
      </div>
    </div>
  );
}

function TemplateBlock({ title, kind, subject, body, fromEmail, onSubject, onBody, onFromEmail, testid }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-3" data-testid={`template-block-${testid}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-zinc-500" />
          <div className="text-sm font-semibold text-zinc-900">{title}</div>
        </div>
        {kind && (
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            data-testid={`${testid}-preview-btn`}
            className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-medium rounded-md border border-zinc-300 bg-zinc-50 text-zinc-800 hover:bg-zinc-100 hover:border-zinc-400 transition-colors"
          >
            <Eye size={13} />
            Aperçu de l'email
          </button>
        )}
      </div>
      {/* Iter-196c — sender alias. Must be configured as a verified "Send mail
          as" alias on the authenticated CEO's Gmail account, otherwise Gmail
          rejects the send (we log the error in the reminders-audit page). */}
      <div>
        <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">
          Expéditeur (From) — alias Gmail
        </Label>
        <Input
          value={fromEmail || ""}
          onChange={(e) => onFromEmail(e.target.value)}
          placeholder="facturation@ba-inmotion.com"
          type="email"
          data-testid={`${testid}-from-email`}
        />
        {/* Iter-197b — explicit yellow warning. The previous footnote was too
            light and led the CEO to confuse a Workspace "alternate email" with
            a Gmail "Send mail as" alias. They are NOT the same: an alternate
            email only routes incoming mail to the same inbox; sending FROM
            that address still requires explicit setup in Gmail → Settings →
            Accounts. Without it, Gmail silently overrides the From back to
            the authenticated user's primary identity (exactly the bug
            reported in iter-197 PROD relances). */}
        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-900">
          <AlertCircle size={13} className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold mb-0.5">L'alias doit être configuré comme « Send mail as » dans Gmail</div>
            <div>
              Un simple <em>alias Workspace</em> (alternate email) ne suffit pas.
              Ouvrez{" "}
              <a
                href="https://mail.google.com/mail/u/0/#settings/accounts"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium hover:text-amber-700"
                data-testid={`${testid}-gmail-settings-link`}
              >
                Gmail → Paramètres → Comptes et importation → « Envoyer des e-mails en tant que »
              </a>{" "}
              et ajoutez l'adresse avec vérification. Sans cette étape, Gmail remplace silencieusement
              l'expéditeur par l'adresse principale du compte qui authentifie l'envoi (le CEO).
            </div>
            <div className="mt-1 text-amber-800/80">
              Laisser vide pour utiliser l'adresse principale par défaut.
            </div>
          </div>
        </div>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">Sujet</Label>
        <Input
          value={subject || ""}
          onChange={(e) => onSubject(e.target.value)}
          data-testid={`${testid}-subject`}
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">Corps</Label>
        <Textarea
          value={body || ""}
          onChange={(e) => onBody(e.target.value)}
          rows={7}
          data-testid={`${testid}-body`}
          className="font-mono text-xs"
        />
        {/* Iter-200j — Live indicator showing where the auto-injected
            "Déclarer le paiement de cette facture" CTA will be placed. */}
        <div className="mt-2">
          <CTAInsertionHint text={body || ""} />
        </div>
      </div>
      <div className="text-[10px] text-zinc-400 font-mono flex items-start gap-1.5">
        <AlertCircle size={11} className="shrink-0 mt-0.5" />
        <span>{VARS_HINT}</span>
      </div>
      {previewOpen && (
        <EmailPreviewModal
          kind={kind}
          title={title}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}


// Iter-200e — Modal that fetches the rendered email (subject + body_html
// with the CTA banner) from /admin/invoice-payment-reminders/preview, then
// renders body_html inside a sandboxed iframe. The "Déclarer mon paiement"
// CTA inside the iframe is a real link to the preview payment-declare page,
// which uses an in-memory invoice (no DB write, no email side-effect).
function EmailPreviewModal({ kind, title, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .post("/admin/invoice-payment-reminders/preview", { kind })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Échec génération de l'aperçu."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind]);

  const fullHtml = data?.body_html
    ? `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>body{margin:0;padding:24px;background:#F4F4F5;font-family:Arial,Helvetica,sans-serif;color:#1A1F2C;}</style></head><body>${data.body_html}</body></html>`
    : "";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      data-testid={`email-preview-modal-${kind}`}
    >
      <div
        className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-zinc-200">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Aperçu</div>
            <div className="font-semibold text-zinc-900 mt-0.5">{title}</div>
            {data?.subject && (
              <div className="mt-3 space-y-1 text-xs font-mono">
                <div className="flex gap-2">
                  <span className="text-zinc-500 shrink-0 w-14">De :</span>
                  <span className="text-zinc-800 truncate">{data.from_email}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-zinc-500 shrink-0 w-14">À :</span>
                  <span className="text-zinc-800 truncate">{data.to_email} <span className="text-zinc-400">(démo)</span></span>
                </div>
                <div className="flex gap-2">
                  <span className="text-zinc-500 shrink-0 w-14">Sujet :</span>
                  <span className="text-zinc-900 font-semibold">{data.subject}</span>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid={`email-preview-close-${kind}`}
            className="rounded-md p-1.5 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 shrink-0"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center h-64 text-sm text-zinc-500 gap-2">
              <Loader2 size={16} className="animate-spin" /> Génération de l'aperçu…
            </div>
          )}
          {error && (
            <div className="m-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
            </div>
          )}
          {!loading && !error && fullHtml && (
            <iframe
              title="Aperçu email"
              srcDoc={fullHtml}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              className="w-full h-[60vh] bg-zinc-50"
              data-testid={`email-preview-iframe-${kind}`}
            />
          )}
        </div>

        {/* Footer with the live CTA URL */}
        {data?.payment_declaration_url && (
          <div className="border-t border-zinc-200 px-5 py-4 bg-zinc-50 rounded-b-xl">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1">
                  Lien CTA — Mode APERÇU (expire dans {data.preview_token_ttl_minutes || 15} min)
                </div>
                <div className="text-[11px] font-mono text-zinc-600 truncate" title={data.payment_declaration_url}>
                  {data.payment_declaration_url}
                </div>
              </div>
              <a
                href={data.payment_declaration_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`email-preview-open-cta-${kind}`}
                className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium rounded-md bg-zinc-950 text-white hover:bg-zinc-800 shrink-0"
              >
                <ExternalLink size={13} />
                Ouvrir la page de déclaration
              </a>
            </div>
            <div className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
              La page s'ouvre en mode aperçu : la facture est en mémoire (jamais persistée), aucune écriture en base ni envoi d'email réel — vous pouvez tester tout le parcours en toute sécurité.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Iter-195o — Exclusions panel. Lists every client and every mission where
// automatic payment reminders are currently paused, with an "Add exclusion"
// picker. Pattern mirrors the End-of-month auto-invoice settings page so the
// CEO sees the same UX across cross-cutting kill-switches.
function ExclusionsPanel() {
  const [overview, setOverview] = useState(null);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addMode, setAddMode] = useState(null); // "client" | "mission"
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [ov, tg] = await Promise.all([
        api.get("/admin/invoice-payment-reminders/paused-overview"),
        api.get("/admin/invoice-payment-reminders/available-targets"),
      ]);
      setOverview(ov.data);
      setTargets(tg.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement exclusions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePaused = async (kind, id, paused, scope = "ALL") => {
    try {
      const path = kind === "client" ? `/clients/${id}/reminders-paused` : `/missions/${id}/reminders-paused`;
      const body = paused ? { paused: true, scope } : { paused: false };
      await api.patch(path, body);
      toast.success(paused
        ? `Exclusion enregistrée (${scope === "ALL" ? "complète" : scope === "MILESTONE" ? "milestones" : "dépassement"})`
        : "Exclusion supprimée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };
  // Iter-195s — picker also lets the CEO choose the scope before adding.
  const [pickScope, setPickScope] = useState("ALL");

  if (loading && !overview) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-5 text-sm text-zinc-500">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement des exclusions…
      </div>
    );
  }
  if (!overview || !targets) return null;

  const pausedClientIds = new Set((overview.clients || []).map((c) => c.client_id));
  const pausedMissionIds = new Set((overview.missions || []).map((m) => m.mission_id));

  const filteredClientCandidates = (targets.clients || [])
    .filter((c) => !pausedClientIds.has(c.client_id))
    .filter((c) => !query || (c.name || "").toLowerCase().includes(query.toLowerCase()))
    .slice(0, 50);
  const filteredMissionCandidates = (targets.missions || [])
    .filter((m) => !pausedMissionIds.has(m.mission_id))
    .filter((m) => !query
      || (m.title || "").toLowerCase().includes(query.toLowerCase())
      || (targets.clients.find((c) => c.client_id === m.client_id)?.name || "").toLowerCase().includes(query.toLowerCase()))
    .slice(0, 50);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-5" data-testid="reminder-exclusions-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
            <Pause size={14} /> Exceptions de relance automatique
          </div>
          <div className="text-xs text-zinc-500 max-w-xl">
            Mettez en pause le cron de relance pour un client ou une mission entière (toutes les factures rattachées sont concernées). Les relances manuelles restent disponibles via le bouton "Relancer" sur chaque facture. Pour exclure une facture précise, utilisez le bouton "Pause" directement sur la ligne de la facture dans le module Factures.
          </div>
        </div>
      </div>

      {/* Paused clients */}
      <div className="border-t border-zinc-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-zinc-700 uppercase tracking-wider flex items-center gap-1.5">
            <Building2 size={12} /> Clients exclus ({overview.clients.length})
          </div>
          <button
            onClick={() => { setAddMode(addMode === "client" ? null : "client"); setQuery(""); }}
            data-testid="reminder-exclude-client-add"
            className="inline-flex items-center gap-1 px-2 h-7 border border-zinc-200 text-xs text-zinc-700 rounded hover:bg-zinc-50"
          >
            <Plus size={11} /> Ajouter
          </button>
        </div>
        {overview.clients.length === 0 ? (
          <div className="text-xs text-zinc-400 italic">Aucun client exclu — les relances s'appliquent à tous les clients.</div>
        ) : (
          <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded">
            {overview.clients.map((c) => (
              <li key={c.client_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`reminder-exclude-client-${c.client_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-zinc-800 truncate">{c.name}</span>
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-900 shrink-0"
                    title={SCOPE_FULL[c.scope] || c.scope}
                    data-testid={`reminder-exclude-client-scope-${c.client_id}`}
                  >
                    Pause {SCOPE_LABEL[c.scope] || c.scope}
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`reminder-exclude-client-menu-${c.client_id}`}
                      className="inline-flex items-center gap-1 px-2 h-7 text-[11px] text-zinc-600 border border-zinc-200 rounded hover:bg-zinc-50"
                      title="Modifier la portée ou réactiver"
                    >
                      Modifier
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {["MILESTONE", "OVERDUE", "ALL"].map((s) => (
                      <DropdownMenuItem
                        key={s}
                        onSelect={(e) => { e.preventDefault(); togglePaused("client", c.client_id, true, s); }}
                        data-testid={`reminder-exclude-client-scope-set-${s.toLowerCase()}-${c.client_id}`}
                        disabled={c.scope === s}
                      >
                        {c.scope === s ? "✓ " : ""}Pause {SCOPE_FULL[s]}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); togglePaused("client", c.client_id, false); }}
                      data-testid={`reminder-exclude-client-remove-${c.client_id}`}
                    >
                      <X size={11} className="mr-1" /> Réactiver toutes les relances
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Paused missions */}
      <div className="border-t border-zinc-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-zinc-700 uppercase tracking-wider flex items-center gap-1.5">
            <Briefcase size={12} /> Missions exclues ({overview.missions.length})
          </div>
          <button
            onClick={() => { setAddMode(addMode === "mission" ? null : "mission"); setQuery(""); }}
            data-testid="reminder-exclude-mission-add"
            className="inline-flex items-center gap-1 px-2 h-7 border border-zinc-200 text-xs text-zinc-700 rounded hover:bg-zinc-50"
          >
            <Plus size={11} /> Ajouter
          </button>
        </div>
        {overview.missions.length === 0 ? (
          <div className="text-xs text-zinc-400 italic">Aucune mission exclue.</div>
        ) : (
          <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded">
            {overview.missions.map((m) => (
              <li key={m.mission_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`reminder-exclude-mission-${m.mission_id}`}>
                <div className="min-w-0 flex-1 flex items-center gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-800 truncate">{m.title}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{m.client_name} · {m.status}</div>
                  </div>
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-900 shrink-0"
                    title={SCOPE_FULL[m.scope] || m.scope}
                    data-testid={`reminder-exclude-mission-scope-${m.mission_id}`}
                  >
                    Pause {SCOPE_LABEL[m.scope] || m.scope}
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`reminder-exclude-mission-menu-${m.mission_id}`}
                      className="inline-flex items-center gap-1 px-2 h-7 text-[11px] text-zinc-600 border border-zinc-200 rounded hover:bg-zinc-50"
                      title="Modifier la portée ou réactiver"
                    >
                      Modifier
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {["MILESTONE", "OVERDUE", "ALL"].map((s) => (
                      <DropdownMenuItem
                        key={s}
                        onSelect={(e) => { e.preventDefault(); togglePaused("mission", m.mission_id, true, s); }}
                        data-testid={`reminder-exclude-mission-scope-set-${s.toLowerCase()}-${m.mission_id}`}
                        disabled={m.scope === s}
                      >
                        {m.scope === s ? "✓ " : ""}Pause {SCOPE_FULL[s]}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); togglePaused("mission", m.mission_id, false); }}
                      data-testid={`reminder-exclude-mission-remove-${m.mission_id}`}
                    >
                      <X size={11} className="mr-1" /> Réactiver toutes les relances
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add picker */}
      {addMode && (
        <div className="border-t border-zinc-100 pt-4" data-testid="reminder-exclude-picker">
          <div className="text-xs font-semibold text-zinc-700 mb-2">
            {addMode === "client" ? "Choisir un client à exclure" : "Choisir une mission à exclure"}
          </div>
          {/* Iter-195t — scope chips applied to whichever entry the CEO picks below */}
          <div className="flex items-center gap-1.5 mb-2" data-testid="reminder-exclude-pick-scope">
            <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mr-1">Portée :</span>
            {[
              { k: "MILESTONE", label: "Milestones" },
              { k: "OVERDUE",   label: "Dépassement" },
              { k: "ALL",       label: "Les deux" },
            ].map((opt) => (
              <button
                key={opt.k}
                type="button"
                onClick={() => setPickScope(opt.k)}
                data-testid={`reminder-exclude-pick-scope-${opt.k.toLowerCase()}`}
                className={`px-2 h-7 text-[11px] font-mono rounded border transition ${
                  pickScope === opt.k
                    ? "border-amber-400 bg-amber-50 text-amber-900 font-semibold"
                    : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="relative mb-2">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={addMode === "client" ? "Rechercher un client…" : "Rechercher une mission…"}
              className="pl-8 h-9 text-sm"
              data-testid="reminder-exclude-search"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto border border-zinc-100 rounded">
            {addMode === "client" ? (
              filteredClientCandidates.length === 0 ? (
                <div className="px-3 py-4 text-xs text-zinc-400 italic text-center">Aucun résultat.</div>
              ) : filteredClientCandidates.map((c) => (
                <button
                  key={c.client_id}
                  onClick={() => togglePaused("client", c.client_id, true, pickScope)}
                  data-testid={`reminder-exclude-client-pick-${c.client_id}`}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-50 last:border-0 text-sm"
                >
                  {c.name}
                </button>
              ))
            ) : (
              filteredMissionCandidates.length === 0 ? (
                <div className="px-3 py-4 text-xs text-zinc-400 italic text-center">Aucun résultat.</div>
              ) : filteredMissionCandidates.map((m) => {
                const cname = (targets.clients.find((c) => c.client_id === m.client_id) || {}).name || "—";
                return (
                  <button
                    key={m.mission_id}
                    onClick={() => togglePaused("mission", m.mission_id, true, pickScope)}
                    data-testid={`reminder-exclude-mission-pick-${m.mission_id}`}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-50 last:border-0 text-sm"
                  >
                    <div className="font-medium text-zinc-800">{m.title}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{cname} · {m.status}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
